import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCaptureFields } from '@/components/vsleads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';

interface TextsTabProps {
  form: UseFormReturn<FormCaptureFields>;
}

export function TextsTab({ form }: TextsTabProps) {
  useLanguage();
  const content = form.watch('content');

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Título (Headline)")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("O texto principal que aparece no formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="headline">{translateInterfaceText("Texto do Título")}</Label>
            <Input
              id="headline"
              value={content.headline.text}
              onChange={(e) =>
                form.setValue('content.headline.text', e.target.value, { shouldDirty: true })
              }
              placeholder={translateInterfaceText("Preencha para acessar o vídeo")}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Posicionamento")}</Label>
            <RadioGroup
              value={content.headline.placement}
              onValueChange={(value: 'inside' | 'outside') =>
                form.setValue('content.headline.placement', value, { shouldDirty: true })
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="inside" id="inside" />
                <Label htmlFor="inside">{translateInterfaceText("Dentro do formulário")}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="outside" id="outside" />
                <Label htmlFor="outside">{translateInterfaceText("Fora (acima do formulário)")}</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Descrição / Rodapé")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Texto adicional de segurança ou informação")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={content.footer}
            onChange={(e) =>
              form.setValue('content.footer', e.target.value, { shouldDirty: true })
            }
            placeholder={translateInterfaceText("Seus dados estão seguros conosco.")}
            rows={2}
          />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Botão Principal")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Texto do botão de envio do formulário")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={content.buttonText}
            onChange={(e) =>
              form.setValue('content.buttonText', e.target.value, { shouldDirty: true })
            }
            placeholder={translateInterfaceText("Acessar vídeo")}
          />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Botão \"Pular\"")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Permite que o usuário continue sem preencher o formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="skip-enabled">{translateInterfaceText("Habilitar botão de pular")}</Label>
            <Switch
              id="skip-enabled"
              checked={content.skip.enabled}
              onCheckedChange={(checked) =>
                form.setValue('content.skip.enabled', checked, { shouldDirty: true })
              }
            />
          </div>

          {content.skip.enabled && (
            <div>
              <Label htmlFor="skip-text">{translateInterfaceText("Texto do botão")}</Label>
              <Input
                id="skip-text"
                value={content.skip.text}
                onChange={(e) =>
                  form.setValue('content.skip.text', e.target.value, { shouldDirty: true })
                }
                placeholder={translateInterfaceText("Ignorar")}
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
